import { useMemo } from 'react'

import type { ClientUiPublic } from '../../api/platformConfig'
import { isWebtoonLayout } from '../../lib/comicPageLayout'
import { ensureEpisodePages } from '../../lib/comicPages'
import { type ComicPanelAspectPreset,effectiveCandidatesPerGeneration } from '../../lib/comicProjectSettings'
import type { ComicEpisode } from '../../lib/comicTypes'
import type { ComicProject } from '../../lib/comicTypes'
import { ThemeReveal, ThemeStagger, ThemeStaggerItem } from '../motion/ThemeMotion'
import { ComicEpisodeCanvasEpisodeHeader } from './ComicEpisodeCanvasEpisodeHeader'
import { ComicEpisodeCanvasEpisodeSection } from './ComicEpisodeCanvasEpisodeSection'
import { ComicPagePreviewCard } from './ComicPagePreviewCard'
import { comicSectionLabelClass } from './comicPanelTokens'

type Props = {
  angular: boolean
  episodes: ComicEpisode[]
  canvasEpisodeIds: string[]
  activeEpisodeId: string
  panelAspect: ComicPanelAspectPreset
  project: ComicProject
  clientUi: ClientUiPublic
  selectedPanelId: string | null
  selectedPageId: string | null
  generatingEpisodeId: string | null
  generatingPageId?: string | null
  platformReady: boolean
  onGenerateEpisode?: (episodeId: string) => void
  onExportEpisode?: (episodeId: string) => void
  onExportEpisodePdf?: (episodeId: string) => void
  exportingEpisodeId?: string | null
  exportingEpisodePdfId?: string | null
  onSelectPage: (pageId: string) => void
  onSelectPanel: (panelId: string) => void
  onExportPage?: (pageId: string) => void
  exportingPageId?: string | null
  onOpenCandidates: (panelId: string) => void
  onSelectPanelCandidate?: (panelId: string, candidateId: string) => void
  onOpenWebtoonReader?: (episodeId: string) => void
  onOpenPageCandidates?: (pageId: string) => void
  onEditPageImage?: (pageId: string) => void
  onOpenImagePreview?: (payload: {
    pageId: string
    imageUrl: string
    panelId: string | null
    scope: 'page' | 'panel'
  }) => void
}

export function ComicEpisodeCanvasList({
  angular,
  episodes,
  canvasEpisodeIds,
  activeEpisodeId: _activeEpisodeId,
  panelAspect,
  project,
  clientUi,
  selectedPanelId,
  selectedPageId,
  generatingEpisodeId,
  generatingPageId = null,
  platformReady,
  onGenerateEpisode,
  onExportEpisode,
  onExportEpisodePdf,
  exportingEpisodeId = null,
  exportingEpisodePdfId = null,
  onSelectPage,
  onSelectPanel,
  onExportPage,
  exportingPageId = null,
  onOpenCandidates,
  onSelectPanelCandidate: _onSelectPanelCandidate,
  onOpenWebtoonReader,
  onOpenPageCandidates,
  onEditPageImage,
  onOpenImagePreview,
}: Props) {
  const canvasEpisodes = useMemo(() => {
    const idSet = new Set(canvasEpisodeIds)
    return episodes.filter((ep) => idSet.has(ep.id))
  }, [episodes, canvasEpisodeIds])

  if (canvasEpisodes.length === 0) {
    return (
      <ThemeReveal className="flex min-h-[min(50vh,24rem)] flex-col items-center justify-center px-6 text-center">
        <p className={comicSectionLabelClass()}>画布预览</p>
      </ThemeReveal>
    )
  }

  return (
    <>
      {canvasEpisodes.map((ep, episodeIndex) => {
        const normalized = ensureEpisodePages(ep)
        const generating = generatingEpisodeId === ep.id
        const hasWebtoonPages = normalized.pages.some((p) => isWebtoonLayout(p.layout))
        const candidatesPerGeneration = effectiveCandidatesPerGeneration(
          project.settings,
          clientUi,
        )
        const exportBusy =
          exportingEpisodeId != null || exportingEpisodePdfId != null

        return (
          <ComicEpisodeCanvasEpisodeSection
            key={ep.id}
            episodeId={ep.id}
            episodeIndex={episodeIndex}
            angular={angular}
            header={
              <ComicEpisodeCanvasEpisodeHeader
                angular={angular}
                episodeId={ep.id}
                title={ep.title}
                summary={ep.summary}
                plotAdvance={ep.plotAdvance}
                plannedPageCount={ep.plannedPageCount}
                pageCount={normalized.pages.length}
                generating={generating}
                platformReady={platformReady}
                hasWebtoonPages={hasWebtoonPages}
                exportBusy={exportBusy}
                exportingEpisode={exportingEpisodeId === ep.id}
                exportingEpisodePdf={exportingEpisodePdfId === ep.id}
                onOpenWebtoonReader={
                  onOpenWebtoonReader ? () => onOpenWebtoonReader(ep.id) : undefined
                }
                onExportEpisode={onExportEpisode ? () => onExportEpisode(ep.id) : undefined}
                onExportEpisodePdf={
                  onExportEpisodePdf ? () => onExportEpisodePdf(ep.id) : undefined
                }
                onGenerateEpisode={
                  onGenerateEpisode ? () => onGenerateEpisode(ep.id) : undefined
                }
              />
            }
          >
            <ThemeStagger indexStagger className="flex flex-col gap-5">
              {normalized.pages.map((page, pageIndex) => (
                <ThemeStaggerItem key={page.id} index={pageIndex} className="min-w-0">
                  <ComicPagePreviewCard
                    angular={angular}
                    episode={normalized}
                    page={page}
                    panelAspect={panelAspect}
                    candidatesPerGeneration={candidatesPerGeneration}
                    selectedPanelId={selectedPanelId}
                    selectedPageId={selectedPageId}
                    onSelectPage={onSelectPage}
                    onSelectPanel={onSelectPanel}
                    onOpenCandidates={onOpenCandidates}
                    onOpenPageCandidates={onOpenPageCandidates}
                    onEditPageImage={onEditPageImage}
                    onExportPage={onExportPage}
                    exportDisabled={
                      exportingPageId != null ||
                      exportingEpisodeId != null ||
                      exportingEpisodePdfId != null
                    }
                    generatingPageId={generatingPageId}
                    onOpenImagePreview={onOpenImagePreview}
                  />
                </ThemeStaggerItem>
              ))}
            </ThemeStagger>
          </ComicEpisodeCanvasEpisodeSection>
        )
      })}
    </>
  )
}
